import { useCallback, useEffect, useState } from "react";
import { History, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

type Run = {
  id: string;
  run_id: string;
  rule_key: string;
  label: string | null;
  trigger_source: string;
  sample: number;
  reached: number;
  conversion_pct: number;
  threshold_pct: number;
  baseline_pct?: number | null;
  baseline_sample?: number | null;
  z_score?: number | null;
  anomaly?: boolean;
  min_sample: number;
  window_hours: number;
  enough_data: boolean;
  breached: boolean;
  alerted: boolean;
  escalated?: boolean;
  repeat_count?: number;
  skipped_by_cooldown: boolean;
  checked_at: string;
};

const outcome = (r: Run) =>
  r.escalated
    ? { text: `Escalated · repeat ${r.repeat_count ?? 0}`, cls: "text-destructive font-semibold" }
    : r.alerted
    ? { text: "Alert sent", cls: "text-orange font-semibold" }
    : r.skipped_by_cooldown
      ? { text: "Below threshold · cooldown", cls: "text-orange/80" }
      : !r.enough_data
        ? { text: "Not enough data", cls: "text-muted-foreground" }
        : { text: "OK", cls: "text-foreground" };

export function FunnelAlertRunLog() {
  const [runs, setRuns] = useState<Run[]>([]);
  const [onlyAlerts, setOnlyAlerts] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    let q = supabase
      .from("funnel_alert_runs" as never)
      .select("*")
      .order("checked_at", { ascending: false })
      .limit(200);
    if (onlyAlerts) q = q.or("alerted.eq.true,escalated.eq.true");
    const { data, error } = await q;
    setError(error?.message ?? null);
    setRuns((data ?? []) as unknown as Run[]);
  }, [onlyAlerts]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="mt-10 border border-border p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display uppercase tracking-wide text-lg">
          <History className="h-5 w-5 text-orange" /> Alert run log
        </h2>
        <div className="flex items-center gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={onlyAlerts} onChange={(e) => setOnlyAlerts(e.target.checked)} />
            Only triggered alerts
          </label>
          <button type="button" onClick={load} className="inline-flex items-center gap-1 hover:text-orange">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mt-1">
        Every rule check (hourly and "Run check now"), the numbers it used, and the result. Latest 200.
      </p>
      {error && <p className="text-sm text-destructive mt-3">{error}</p>}
      <div className="overflow-x-auto mt-4">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="py-2 pr-3">Checked</th>
              <th className="pr-3">Rule</th>
              <th className="pr-3">Trigger</th>
              <th className="pr-3 text-right">Reached / Sample</th>
              <th className="pr-3 text-right">Conversion</th>
              <th className="pr-3 text-right">Threshold</th>
              <th className="pr-3 text-right">Baseline · z</th>
              <th className="pr-3 text-right">Min sample · Window</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && (
              <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">No runs logged yet.</td></tr>
            )}
            {runs.map((r) => {
              const o = outcome(r);
              return (
                <tr key={r.id} className={cn("border-t border-border tabular-nums", (r.alerted || r.escalated) && "bg-orange/5")}>
                  <td className="py-2 pr-3 whitespace-nowrap">{new Date(r.checked_at).toLocaleString()}</td>
                  <td className="pr-3">{r.label ?? r.rule_key}</td>
                  <td className="pr-3 capitalize">{r.trigger_source}</td>
                  <td className="pr-3 text-right">{r.reached} / {r.sample}</td>
                  <td className="pr-3 text-right">{r.enough_data ? `${r.conversion_pct}%` : "—"}</td>
                  <td className="pr-3 text-right">{r.threshold_pct}%</td>
                  <td className="pr-3 text-right whitespace-nowrap">
                    {r.baseline_pct == null ? "—" : `${r.baseline_pct}% (n=${r.baseline_sample ?? 0})`}
                    {r.z_score != null && <span className={cn("block text-xs", r.anomaly ? "text-destructive font-semibold" : "text-muted-foreground")}>z={r.z_score}{r.anomaly ? " · significant" : ""}</span>}
                  </td>
                  <td className="pr-3 text-right">{r.min_sample} · {r.window_hours}h</td>
                  <td className={o.cls}>{o.text}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
